import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CodeBracketIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  HeartIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  ClockIcon,
  StarIcon as StarOutlineIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { ArrowRight } from 'lucide-react';
import { usePubPackages } from '../../contexts/PubPackageContext';
import { useStarterTemplates } from '../../contexts/StarterTemplateContext';
import { useBlogs } from '../../contexts/BlogContext';

const OpenSourcePage: React.FC = () => {
  const { featuredPubPackages, pubPackages, loading: pubPackagesLoading } = usePubPackages();
  const { featuredStarterTemplates, starterTemplates, loading: templatesLoading } = useStarterTemplates();
  const { featuredBlogs, blogs, loading: blogsLoading } = useBlogs();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (pubPackagesLoading || templatesLoading || blogsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-indigo-900 dark:to-blue-900">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-indigo-900 dark:to-blue-900">
      {/* Hero Section */}
      <section className="py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-4">
              <div className="h-1 w-12 bg-primary-500 mr-4"></div>
              <span className="text-primary-600 dark:text-primary-400 font-medium tracking-wide uppercase text-sm">
                Open Source
              </span>
              <div className="h-1 w-12 bg-primary-500 ml-4"></div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Open Source Contributions
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
              Empowering the developer community with packages, tools, and comprehensive tutorials 
              that accelerate development and foster innovation.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {pubPackages.length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Flutter Packages</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {starterTemplates.length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Dev Tools & Templates</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {blogs.length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Coding Tutorials</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                  {pubPackages.reduce((total, pkg) => total + (pkg.downloads || 0), 0) + 
                   starterTemplates.reduce((total, template) => total + (template.downloads || 0), 0)}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Total Downloads</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Pub Packages */}
            <motion.div
              variants={itemVariants}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-xl">
                  <CodeBracketIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Flutter Packages
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Published on pub.dev
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                High-quality Flutter packages that solve real-world development challenges and speed up app development.
              </p>

              {featuredPubPackages.slice(0, 3).map((pkg) => (
                <div key={pkg.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {pkg.name}
                    </h4>
                    <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {pkg.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                        {pkg.downloads ? (pkg.downloads >= 1000 ? `${(pkg.downloads/1000).toFixed(1)}K` : pkg.downloads) : 0}
                      </span>
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {pkg.likes || 0}
                      </span>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400">
                      v{pkg.version}
                    </span>
                  </div>
                </div>
              ))}

              {featuredPubPackages.length === 0 && (
                <div className="text-center py-8 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <CodeBracketIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No packages published yet — check back soon.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Link
                  to="/pub-packages"
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                >
                  View All Packages
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Development Tools & Templates */}
            <motion.div
              variants={itemVariants}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl">
                  <WrenchScrewdriverIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Dev Tools & Templates
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Starter templates & tools
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Ready-to-use templates, extensions, and development tools to accelerate your development workflow.
              </p>

              {featuredStarterTemplates.slice(0, 3).map((template) => (
                <div key={template.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {template.icon ? (
                        <img
                          src={template.icon}
                          alt={template.title}
                          className="w-8 h-8 rounded object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                          <WrenchScrewdriverIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                      )}
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {template.title}
                      </h4>
                    </div>
                    <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                      {template.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                        {template.downloads ? (template.downloads >= 1000 ? `${(template.downloads/1000).toFixed(1)}K` : template.downloads) : 0}
                      </span>
                      <span className="flex items-center">
                        <StarOutlineIcon className="h-3 w-3 mr-1" />
                        {template.stars || 0}
                      </span>
                    </div>
                    <span className="text-purple-600 dark:text-purple-400">
                      {template.category}
                    </span>
                  </div>
                </div>
              ))}

              {featuredStarterTemplates.length === 0 && (
                <div className="text-center py-8 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <WrenchScrewdriverIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No tools or templates added yet — check back soon.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Link
                  to="/starter-templates"
                  className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
                >
                  Explore Tools & Templates
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Coding Tutorials */}
            <motion.div
              variants={itemVariants}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-br from-green-500 to-blue-600 p-4 rounded-xl">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Coding Tutorials & Tech Articles
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Bengali & English
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Comprehensive coding tutorials with YouTube videos, covering everything from basics to advanced concepts.
              </p>

              {featuredBlogs.slice(0, 3).map((blog) => (
                <div key={blog.id} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                      {blog.title}
                    </h4>
                    <div className="flex items-center space-x-1">
                      {blog.youtubeVideoId && (
                        <PlayIcon className="h-4 w-4 text-red-600" />
                      )}
                      {blog.featured && (
                        <StarSolidIcon className="h-4 w-4 text-yellow-400" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {blog.readingTime} min
                      </span>
                      <span className="flex items-center">
                        <HeartIcon className="h-3 w-3 mr-1" />
                        {blog.likes || 0}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        blog.difficulty === 'Beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        blog.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {blog.difficulty}
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {blog.language}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {featuredBlogs.length === 0 && (
                <div className="text-center py-8 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No tutorials or articles published yet — check back soon.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Link
                  to="/blogs"
                  className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                >
                  Read All Tutorials
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                🚀 Let's Build Something Amazing Together!
              </h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Explore my open source contributions and join the community of developers 
                building the future of software development.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/pub-packages"
                  className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Flutter Packages
                </Link>
                <Link
                  to="/starter-templates"
                  className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Dev Tools
                </Link>
                <Link
                  to="/blogs"
                  className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Tutorials
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OpenSourcePage; 